import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  AS_ON,
  CURRENT_RATIO,
  DE,
  EBITDA,
  EBITDA_MARGIN,
  GROSS_BLOCK,
  KRISHNA,
  MADHYA,
  NIRMALA,
  OSTWAL,
  PAT,
  PAT_MARGIN,
  ROCE,
  RONW,
  SEASONS,
  SHRI,
  TOTAL_DEBT,
  TURN_OVER,
  YEAR,
} from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { useForm } from "react-hook-form";
import {
  useAddFinancialReportingMutation,
  useGetFinancialReporting,
} from "@/services";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import moment from "moment";

const useFinancialReporting = () => {
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [OSTWAL]: {
        [YEAR]: "",
        [AS_ON]: "",
        [TURN_OVER]: "",
        [EBITDA]: "",
        [EBITDA_MARGIN]: "",
        [PAT]: "",
        [PAT_MARGIN]: "",
        [GROSS_BLOCK]: "",
        [DE]: "",
        [ROCE]: "",
        [CURRENT_RATIO]: "",
        [TOTAL_DEBT]: "",
        [RONW]: "",
      },
      [MADHYA]: {
        [YEAR]: "",
        [AS_ON]: "",
        [TURN_OVER]: "",
        [EBITDA]: "",
        [EBITDA_MARGIN]: "",
        [PAT]: "",
        [PAT_MARGIN]: "",
        [GROSS_BLOCK]: "",
        [DE]: "",
        [ROCE]: "",
        [CURRENT_RATIO]: "",
        [TOTAL_DEBT]: "",
        [RONW]: "",
      },
      [KRISHNA]: {
        [YEAR]: "",
        [AS_ON]: "",
        [TURN_OVER]: "",
        [EBITDA]: "",
        [EBITDA_MARGIN]: "",
        [PAT]: "",
        [PAT_MARGIN]: "",
        [GROSS_BLOCK]: "",
        [DE]: "",
        [ROCE]: "",
        [CURRENT_RATIO]: "",
        [TOTAL_DEBT]: "",
        [RONW]: "",
      },
      [SHRI]: {
        [YEAR]: "",
        [AS_ON]: "",
        [TURN_OVER]: "",
        [EBITDA]: "",
        [EBITDA_MARGIN]: "",
        [PAT]: "",
        [PAT_MARGIN]: "",
        [GROSS_BLOCK]: "",
        [DE]: "",
        [ROCE]: "",
        [CURRENT_RATIO]: "",
        [TOTAL_DEBT]: "",
        [RONW]: "",
      },
      [SEASONS]: {
        [YEAR]: "",
        [AS_ON]: "",
        [TURN_OVER]: "",
        [EBITDA]: "",
        [EBITDA_MARGIN]: "",
        [PAT]: "",
        [PAT_MARGIN]: "",
        [GROSS_BLOCK]: "",
        [DE]: "",
        [ROCE]: "",
        [CURRENT_RATIO]: "",
        [TOTAL_DEBT]: "",
        [RONW]: "",
      },
      [NIRMALA]: {
        [YEAR]: "",
        [AS_ON]: "",
        [TURN_OVER]: "",
        [EBITDA]: "",
        [EBITDA_MARGIN]: "",
        [PAT]: "",
        [PAT_MARGIN]: "",
        [GROSS_BLOCK]: "",
        [DE]: "",
        [ROCE]: "",
        [CURRENT_RATIO]: "",
        [TOTAL_DEBT]: "",
        [RONW]: "",
      },
    },
  });
  const [yearKey, setYearKey] = useState<number>(Math.random() * 2);

  const requiredFields = getRequiredFields(schema);

  const { data: financeData, isLoading: isLoadingFinance } =
    useGetFinancialReporting({});

  useEffect(() => {
    if (financeData) {
      form.reset({
        [OSTWAL]: {
          [YEAR]: financeData?.data?.[OSTWAL]?.[YEAR]?.toString() || "",
          [AS_ON]: financeData?.data?.[OSTWAL]?.[AS_ON]?.toString() || "",
          [TURN_OVER]:
            financeData?.data?.[OSTWAL]?.[TURN_OVER]?.toString() || "",
          [EBITDA]: financeData?.data?.[OSTWAL]?.[EBITDA]?.toString() || "",
          [EBITDA_MARGIN]:
            financeData?.data?.[OSTWAL]?.[EBITDA_MARGIN]?.toString() || "",
          [PAT]: financeData?.data?.[OSTWAL]?.[PAT]?.toString() || "",
          [PAT_MARGIN]:
            financeData?.data?.[OSTWAL]?.[PAT_MARGIN]?.toString() || "",
          [GROSS_BLOCK]:
            financeData?.data?.[OSTWAL]?.[GROSS_BLOCK]?.toString() || "",
          [DE]: financeData?.data?.[OSTWAL]?.[DE]?.toString() || "",
          [ROCE]: financeData?.data?.[OSTWAL]?.[ROCE]?.toString() || "",
          [CURRENT_RATIO]:
            financeData?.data?.[OSTWAL]?.[CURRENT_RATIO]?.toString() || "",
          [TOTAL_DEBT]:
            financeData?.data?.[OSTWAL]?.[TOTAL_DEBT]?.toString() || "",
          [RONW]: financeData?.data?.[OSTWAL]?.[RONW]?.toString() || "",
        },
        [MADHYA]: {
          [YEAR]: financeData?.data?.[MADHYA]?.[YEAR]?.toString() || "",
          [AS_ON]: financeData?.data?.[MADHYA]?.[AS_ON]?.toString() || "",
          [TURN_OVER]:
            financeData?.data?.[MADHYA]?.[TURN_OVER]?.toString() || "",
          [EBITDA]: financeData?.data?.[MADHYA]?.[EBITDA]?.toString() || "",
          [EBITDA_MARGIN]:
            financeData?.data?.[MADHYA]?.[EBITDA_MARGIN]?.toString() || "",
          [PAT]: financeData?.data?.[MADHYA]?.[PAT]?.toString() || "",
          [PAT_MARGIN]:
            financeData?.data?.[MADHYA]?.[PAT_MARGIN]?.toString() || "",
          [GROSS_BLOCK]:
            financeData?.data?.[MADHYA]?.[GROSS_BLOCK]?.toString() || "",
          [DE]: financeData?.data?.[MADHYA]?.[DE]?.toString() || "",
          [ROCE]: financeData?.data?.[MADHYA]?.[ROCE]?.toString() || "",
          [CURRENT_RATIO]:
            financeData?.data?.[MADHYA]?.[CURRENT_RATIO]?.toString() || "",
          [TOTAL_DEBT]:
            financeData?.data?.[MADHYA]?.[TOTAL_DEBT]?.toString() || "",
          [RONW]: financeData?.data?.[MADHYA]?.[RONW]?.toString() || "",
        },
        [KRISHNA]: {
          [YEAR]: financeData?.data?.[KRISHNA]?.[YEAR]?.toString() || "",
          [AS_ON]: financeData?.data?.[KRISHNA]?.[AS_ON]?.toString() || "",
          [TURN_OVER]:
            financeData?.data?.[KRISHNA]?.[TURN_OVER]?.toString() || "",
          [EBITDA]: financeData?.data?.[KRISHNA]?.[EBITDA]?.toString() || "",
          [EBITDA_MARGIN]:
            financeData?.data?.[KRISHNA]?.[EBITDA_MARGIN]?.toString() || "",
          [PAT]: financeData?.data?.[KRISHNA]?.[PAT]?.toString() || "",
          [PAT_MARGIN]:
            financeData?.data?.[KRISHNA]?.[PAT_MARGIN]?.toString() || "",
          [GROSS_BLOCK]:
            financeData?.data?.[KRISHNA]?.[GROSS_BLOCK]?.toString() || "",
          [DE]: financeData?.data?.[KRISHNA]?.[DE]?.toString() || "",
          [ROCE]: financeData?.data?.[KRISHNA]?.[ROCE]?.toString() || "",
          [CURRENT_RATIO]:
            financeData?.data?.[KRISHNA]?.[CURRENT_RATIO]?.toString() || "",
          [TOTAL_DEBT]:
            financeData?.data?.[KRISHNA]?.[TOTAL_DEBT]?.toString() || "",
          [RONW]: financeData?.data?.[KRISHNA]?.[RONW]?.toString() || "",
        },
        [SHRI]: {
          [YEAR]: financeData?.data?.[SHRI]?.[YEAR]?.toString() || "",
          [AS_ON]: financeData?.data?.[SHRI]?.[AS_ON]?.toString() || "",
          [TURN_OVER]: financeData?.data?.[SHRI]?.[TURN_OVER]?.toString() || "",
          [EBITDA]: financeData?.data?.[SHRI]?.[EBITDA]?.toString() || "",
          [EBITDA_MARGIN]:
            financeData?.data?.[SHRI]?.[EBITDA_MARGIN]?.toString() || "",
          [PAT]: financeData?.data?.[SHRI]?.[PAT]?.toString() || "",
          [PAT_MARGIN]:
            financeData?.data?.[SHRI]?.[PAT_MARGIN]?.toString() || "",
          [GROSS_BLOCK]:
            financeData?.data?.[SHRI]?.[GROSS_BLOCK]?.toString() || "",
          [DE]: financeData?.data?.[SHRI]?.[DE]?.toString() || "",
          [ROCE]: financeData?.data?.[SHRI]?.[ROCE]?.toString() || "",
          [CURRENT_RATIO]:
            financeData?.data?.[SHRI]?.[CURRENT_RATIO]?.toString() || "",
          [TOTAL_DEBT]:
            financeData?.data?.[SHRI]?.[TOTAL_DEBT]?.toString() || "",
          [RONW]: financeData?.data?.[SHRI]?.[RONW]?.toString() || "",
        },
        [SEASONS]: {
          [YEAR]: financeData?.data?.[SEASONS]?.[YEAR]?.toString() || "",
          [AS_ON]: financeData?.data?.[SEASONS]?.[AS_ON]?.toString() || "",
          [TURN_OVER]:
            financeData?.data?.[SEASONS]?.[TURN_OVER]?.toString() || "",
          [EBITDA]: financeData?.data?.[SEASONS]?.[EBITDA]?.toString() || "",
          [EBITDA_MARGIN]:
            financeData?.data?.[SEASONS]?.[EBITDA_MARGIN]?.toString() || "",
          [PAT]: financeData?.data?.[SEASONS]?.[PAT]?.toString() || "",
          [PAT_MARGIN]:
            financeData?.data?.[SEASONS]?.[PAT_MARGIN]?.toString() || "",
          [GROSS_BLOCK]:
            financeData?.data?.[SEASONS]?.[GROSS_BLOCK]?.toString() || "",
          [DE]: financeData?.data?.[SEASONS]?.[DE]?.toString() || "",
          [ROCE]: financeData?.data?.[SEASONS]?.[ROCE]?.toString() || "",
          [CURRENT_RATIO]:
            financeData?.data?.[SEASONS]?.[CURRENT_RATIO]?.toString() || "",
          [TOTAL_DEBT]:
            financeData?.data?.[SEASONS]?.[TOTAL_DEBT]?.toString() || "",
          [RONW]: financeData?.data?.[SEASONS]?.[RONW]?.toString() || "",
        },
        [NIRMALA]: {
          [YEAR]: financeData?.data?.[NIRMALA]?.[YEAR]?.toString() || "",
          [AS_ON]: financeData?.data?.[NIRMALA]?.[AS_ON]?.toString() || "",
          [TURN_OVER]:
            financeData?.data?.[NIRMALA]?.[TURN_OVER]?.toString() || "",
          [EBITDA]: financeData?.data?.[NIRMALA]?.[EBITDA]?.toString() || "",
          [EBITDA_MARGIN]:
            financeData?.data?.[NIRMALA]?.[EBITDA_MARGIN]?.toString() || "",
          [PAT]: financeData?.data?.[NIRMALA]?.[PAT]?.toString() || "",
          [PAT_MARGIN]:
            financeData?.data?.[NIRMALA]?.[PAT_MARGIN]?.toString() || "",
          [GROSS_BLOCK]:
            financeData?.data?.[NIRMALA]?.[GROSS_BLOCK]?.toString() || "",
          [DE]: financeData?.data?.[NIRMALA]?.[DE]?.toString() || "",
          [ROCE]: financeData?.data?.[NIRMALA]?.[ROCE]?.toString() || "",
          [CURRENT_RATIO]:
            financeData?.data?.[NIRMALA]?.[CURRENT_RATIO]?.toString() || "",
          [TOTAL_DEBT]:
            financeData?.data?.[NIRMALA]?.[TOTAL_DEBT]?.toString() || "",
          [RONW]: financeData?.data?.[NIRMALA]?.[RONW]?.toString() || "",
        },
      });
      setYearKey(Math.random() * 3);
    }
  }, [financeData]);

  const { mutate: addInfo, isPending: isAddPending } =
    useAddFinancialReportingMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Financial Reporting Added Successfully", {
          className: "!bg-green-600 !text-white",
        });
      },
    });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    const allAsOnFilled = Object.values(data).every(
      (company: any) => company[AS_ON] && company[AS_ON].trim() !== ""
    );

    if (!allAsOnFilled) {
      toast.error("Value for As On is missing", {
        className: "!bg-red-500 !text-white",
      });
      return;
    }

    const updatedData = Object.fromEntries(
      Object.entries(data).map(([company, fields]: [string, any]) => [
        company,
        {
          ...fields,
          [AS_ON]: fields[AS_ON]
            ? moment(fields[AS_ON]).format("MM-DD-YYYY")
            : "",
        },
      ])
    );

    addInfo({ data: { ...updatedData } });
  };

  return {
    form,
    requiredFields,
    onSubmit,
    isLoadingFinance,
    isAddPending,
    yearKey,
  };
};

export default useFinancialReporting;
